import React, { useState, useCallback } from 'react';
import { SatelliteGroup, CloseApproachConfig } from './types/satellite';
import { DEFAULT_CONFIG } from './config/satelliteGroups';
import { useSatelliteData, useCloseApproach } from './hooks/useSatelliteData';
import { useSatelliteVisualization } from './hooks/useSatelliteVisualization';
import { ControlPanel } from './components/ControlPanel';
import { SatelliteGlobe } from './components/SatelliteGlobe';
import { CloseApproachPanel } from './components/CloseApproachPanel';
import { 
  exportSatellitesAsGeoJSON, 
  exportCloseApproachEvents,
  exportSystemStatus 
} from './utils/exportUtils';

export default function App() {
  // State management
  const [currentGroup, setCurrentGroup] = useState<SatelliteGroup>(DEFAULT_CONFIG.defaultGroup);
  const [maxSatellites, setMaxSatellites] = useState(DEFAULT_CONFIG.maxSatellites);
  const [showTracks, setShowTracks] = useState(false);
  const [caConfig, setCAConfig] = useState<CloseApproachConfig>(DEFAULT_CONFIG.closeApproach);
  
  // Custom hooks
  const { 
    satellites, 
    loading, 
    error, 
    loadSatellites, 
    clearError 
  } = useSatelliteData();
  
  const { 
    events: caEvents, 
    running: caRunning, 
    runAnalysis: runCAAnalysis,
    clearEvents: clearCAEvents 
  } = useCloseApproach();
  
  const { 
    globePoints, 
    groundTracks, 
    currentTime 
  } = useSatelliteVisualization(satellites, showTracks);
  
  // Event handlers
  const handleLoadSatellites = useCallback(() => {
    clearError();
    clearCAEvents();
    loadSatellites(currentGroup, maxSatellites);
  }, [currentGroup, maxSatellites, loadSatellites, clearError, clearCAEvents]);
  
  const handleGroupChange = useCallback((group: SatelliteGroup) => {
    setCurrentGroup(group);
  }, []);
  
  const handleRunCAAnalysis = useCallback(() => {
    if (satellites.length > 0) {
      runCAAnalysis(satellites, caConfig);
    }
  }, [satellites, caConfig, runCAAnalysis]);
  
  const handleExportGeoJSON = useCallback(() => {
    exportSatellitesAsGeoJSON(globePoints);
  }, [globePoints]);
  
  const handleExportCA = useCallback(() => {
    exportCloseApproachEvents(caEvents, caConfig);
  }, [caEvents, caConfig]);
  
  const handleExportSystemStatus = useCallback(() => {
    exportSystemStatus(satellites.length, caEvents.length, caConfig, currentTime);
  }, [satellites.length, caEvents.length, caConfig, currentTime]);
  
  const handleSatelliteClick = useCallback((satellite: any) => {
    console.log('Satellite clicked:', satellite);
    // TODO: Implement satellite detail panel or maneuver planning interface
  }, []);
  
  // Load initial data on mount
  React.useEffect(() => {
    loadSatellites(currentGroup, maxSatellites);
  }, []); // Only run once on mount
  
  return (
    <div className="w-full h-screen flex flex-col bg-gray-900">
      {/* Control Panel */}
      <ControlPanel
        currentGroup={currentGroup}
        onGroupChange={handleGroupChange}
        maxSatellites={maxSatellites}
        onMaxSatellitesChange={setMaxSatellites}
        onReload={handleLoadSatellites}
        loading={loading}
        satelliteCount={satellites.length}
        showTracks={showTracks}
        onShowTracksChange={setShowTracks}
        caConfig={caConfig}
        onCAConfigChange={setCAConfig}
        onRunCA={handleRunCAAnalysis}
        caRunning={caRunning}
        caEventCount={caEvents.length}
        onExportGeoJSON={handleExportGeoJSON}
        onExportCA={handleExportCA}
        error={error}
      />
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Globe visualization */}
        <div className="flex-1 min-h-0">
          <SatelliteGlobe
            globePoints={globePoints}
            groundTracks={groundTracks}
            onSatelliteClick={handleSatelliteClick}
            height={window.innerHeight - 320} // Account for control panel + CA panel
          />
        </div>
        
        {/* Close Approach Analysis Panel */}
        <div className="flex-shrink-0">
          <CloseApproachPanel
            events={caEvents}
            thresholdKm={caConfig.thresholdKm}
            horizonSec={caConfig.horizonSec}
          />
        </div>
      </div>
      
      {/* Debug Info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-20 left-4 bg-black/80 text-white p-2 text-xs rounded">
          <div>Satellites: {satellites.length}</div>
          <div>Globe Points: {globePoints.length}</div>
          <div>Ground Tracks: {groundTracks.length}</div>
          <div>CA Events: {caEvents.length}</div>
          <div>Time: {currentTime.toISOString().split('T')[1].split('.')[0]}</div>
          <button 
            onClick={handleExportSystemStatus}
            className="mt-2 px-2 py-1 bg-blue-600 rounded text-xs"
          >
            Export System Status
          </button>
        </div>
      )}
    </div>
  );
}